import React, { useCallback, useMemo, useState } from 'react';

import { Box, IconButton } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { generatePath, useHistory } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import sanitizeHtml from 'sanitize-html';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import YearPlannerActivitiesPrintTemplate from 'components/_print/_templates/YearPlannerActivitiesPrintTemplate/YearPlannerActivitiesPrintTemplate';
import PrintComponent from 'components/_print/PrintComponent/PrintComponent';
import fetchPaginatedPrintData from 'components/_print/utils/fetchPaginatedPrintData';
import ColoredButton from 'components/ColoredButton';
import ContextMenu from 'components/ContextMenu/ContextMenu';
import FooterButton from 'components/FooterButton';
import MonthsDropdown from 'components/MonthsDropdown/MonthsDropdown';
import Table from 'components/Table';
import yearPlannerActivities, { yearPlannerActivitiesApi } from 'config/api/yearPlannerActivities/yearPlannerActivities';
import { formatTimePeriodToString, parseAllYearPlannerActivityForFE } from 'config/api/yearPlannerActivities/yearPlannerActivities.parsers';
import { YearPlannerActivity, YearPlannerActivityDTO } from 'config/api/yearPlannerActivities/yearPlannerActivities.types';
import ROLES from 'config/constants/ROLES';
import DIRECTIONS from 'config/constants/sortDirections';
import EVENTS from 'config/events/pubsub';
import safeT from 'helpers/safeT/safeT';
import useApiCall from 'hooks/useApiCall';
import usePagination from 'hooks/usePagination';
import usePermissions from 'hooks/usePermissions';
import useSubscription from 'hooks/useSubscription';
import assets_messages from 'messages/assets_messages';
import general_messages from 'messages/general_messages';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';
import PATHS from 'router/PATHS';
import StorageService from 'services/StorageService';
import userDataStorage from 'storages/userDataStorage';
import { Id } from 'types/Id';
import { TableDataMappingRow } from 'types/Table';

import TrimHTMLText from '../TrimHTMLText/TrimHTMLText';

// @ts-ignore
const selectedMonthForYearPlannerActivityTable = new StorageService('selectedMonthForYearPlannerActivityTable', {
  storage: 'sessionStorage',
});

export const getActivityDescriptionPreview = (description: string): string =>
  sanitizeHtml(
    description
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<\/li>/gi, '\n'),
    { allowedTags: [], allowedAttributes: {} },
  )
    .replace(/\n{2,}/g, '\n')
    .trim();

type Props = {
  actionAddon?: React.ReactNode;
};

const YearPlannerAssetsActivityTable: React.FC<Props> = ({ actionAddon }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { apiCall } = useApiCall();
  const { enqueueSnackbar } = useSnackbar();

  const [isAssetsManager, hasAssetTaskDashboardAccess] = usePermissions([
    ROLES.ORGANIZATION.ASSETS_MANAGER,
    ROLES.ORGANIZATION.ASSET_TASK_DASHBOARD_ACCESS,
  ]);
  const canPrintYearPlannerActivities = isAssetsManager || hasAssetTaskDashboardAccess || Boolean(userDataStorage.get()?.is_asset_owner);
  // @ts-ignore
  const { showConfirmationModal } = useConfirmationModalContext();

  // @ts-ignore
  const [month, setLocalMonth] = useState<number>(selectedMonthForYearPlannerActivityTable.get() || new Date().getMonth());
  const setMonth = (value: number) => {
    setLocalMonth(value);
    selectedMonthForYearPlannerActivityTable.set(value);
  };

  const [sort, setSort] = useState<{ key: string | null; direction: string | null }>({ key: null, direction: null });
  const onSort = (key: string, direction: string) => setSort({ key, direction });
  const [printActivities, setPrintActivities] = useState<YearPlannerActivity[]>([]);
  const [printCreatedAt, setPrintCreatedAt] = useState(new Date());
  const [printLoading, setPrintLoading] = useState(false);

  const orderBy = useMemo(() => {
    const { key, direction } = sort;
    if (!key || !direction) return null;
    return direction === DIRECTIONS.ASC ? `-${key}` : key;
  }, [sort]);

  const {
    data: tableData,
    getData: getTableData,
    hasNextPage,
    loading,
    refreshData,
  } = usePagination({
    endpointFunc: yearPlannerActivities.getAllAssets,
    limits: [5, 5, 5],
    isGet: true,
    dependencies: [sort, month],
    sort,
    queryParams: { begin__month: month + 1 },
  });

  const refresh = () => {
    refreshData({ queryParamsOverride: { begin__month: month + 1 } });
  };

  const deleteMutation = useMutation(['delete asset activity'], yearPlannerActivitiesApi.removeYearPlannerActivity, {
    onSuccess: refresh,
  });

  const onEdit = (id: Id) => {
    history.push(generatePath(`${PATHS.YEAR_PLANNER_ASSETS_ACTIVITY_EDIT}/:activityId`, { activityId: id }));
  };
  const onDelete = async ({ id, displayName }: YearPlannerActivity) => {
    const shouldProceed = await showConfirmationModal({
      title: t(assets_messages.remove_asset_activity_modal.title),
      body: t(assets_messages.remove_asset_activity_modal.body, { activityName: displayName }),
    });
    if (shouldProceed) {
      deleteMutation.mutate(id);
    }
  };

  const getRowHref = ({ id }: YearPlannerActivity) => generatePath(`${PATHS.YEAR_PLANNER_ASSETS_ACTIVITY}/:activityId`, { activityId: id });

  const renderContextMenu = useCallback(
    (data: YearPlannerActivity) => {
      const elements = [
        { label: t(general_messages.edit), onClick: () => onEdit(data.id) },
        { label: t(general_messages.delete), onClick: () => onDelete(data) },
      ];

      if (!isAssetsManager) return null;
      return (
        <ContextMenu elements={elements} id={`assets-table-row-${data.id}`}>
          <IconButton>
            <MoreIcon />
          </IconButton>
        </ContextMenu>
      );
    },
    [tableData],
  );

  useSubscription(EVENTS.ASSETS_YEAR_PLANNER_UPDATED, refresh);

  const dataMapping: TableDataMappingRow<YearPlannerActivity>[] = useMemo(
    () => [
      {
        id: 'activity__name',
        label: t(assets_messages.activities_table.activity),
        get: ({ activity, displayName }) => (activity ? safeT(t, activity.nameTranslationKey, activity.name) : displayName),
        width: '18%',
      },
      {
        id: 'begin',
        label: t(assets_messages.activities_table.date),
        get: ({ begin, end }) => (
          <div>
            <p>{`${t(assets_messages.activities_table.start_date)}: ${formatTimePeriodToString(begin)}`}</p>
            <p>{`${t(assets_messages.activities_table.end_date)}: ${formatTimePeriodToString(end)}`}</p>
          </div>
        ),
        width: '18%',
      },
      {
        id: 'activity__description',
        label: t(assets_messages.activities_table.description),
        get: ({ activity, displayDescription }) => (
          <TrimHTMLText linesToShow={2}>
            <p style={{ margin: 0, whiteSpace: 'pre-line' }}>
              {activity
                ? getActivityDescriptionPreview(safeT(t, activity.descriptionTranslationKey, activity.description))
                : displayDescription}
            </p>
          </TrimHTMLText>
        ),
        width: '58%',
      },
      {
        id: 'context',
        width: '6%',
        label: '',
        isAddon: true,
        get: (data): React.ReactNode => renderContextMenu(data) || ' ',
        blockSorting: true,
      },
    ],
    [tableData],
  );

  const preparePrint = useCallback(
    async (onPrint: ReturnType<typeof useReactToPrint>) => {
      if (printLoading) return;
      setPrintLoading(true);
      try {
        const rows = await fetchPaginatedPrintData<YearPlannerActivityDTO>({
          endpointFunc: yearPlannerActivities.getAllAssets,
          apiCall,
          queryParams: { begin__month: month + 1, ...(orderBy && { ordering: orderBy }) },
        });
        setPrintActivities(parseAllYearPlannerActivityForFE(rows));
        setPrintCreatedAt(new Date());
        window.setTimeout(() => onPrint?.(), 0);
      } catch (error) {
        enqueueSnackbar(t(general_messages.api_error), { variant: 'error' });
      } finally {
        setPrintLoading(false);
      }
    },
    [apiCall, enqueueSnackbar, month, orderBy, printLoading, t],
  );

  return (
    <>
      <Box alignItems='center' display='flex' flexWrap='wrap' justifyContent='space-between' mb={2}>
        <MonthsDropdown onChange={setMonth} value={month} />
        <Box alignItems='center' display='flex' flexWrap='wrap' gridGap={8} justifyContent='flex-end'>
          {canPrintYearPlannerActivities && (
            <PrintComponent
              ctaRenderer={({ onPrint }) => (
                // @ts-ignore
                <ColoredButton
                  customColor='secondary'
                  disabled={printLoading}
                  onClick={() => preparePrint(onPrint)}
                  showLoader={printLoading}
                  variant='outlined'
                >
                  {t(assets_messages.buttons.print_year_planner)}
                </ColoredButton>
              )}
              documentTitle={t(assets_messages.print.year_planner_title)}
              templateComponent={<YearPlannerActivitiesPrintTemplate activities={printActivities} printedAt={printCreatedAt} />}
            />
          )}
          {actionAddon}
        </Box>
      </Box>
      <Table
        clickableRow
        data={tableData && parseAllYearPlannerActivityForFE(tableData)}
        dataMapping={dataMapping}
        getRowHref={getRowHref}
        onSort={onSort}
        refetching={loading}
        sort={sort}
      />
      {hasNextPage && (
        // @ts-ignore
        <FooterButton loading={loading} onClick={getTableData}>
          {t(general_messages.show_more)}
        </FooterButton>
      )}
    </>
  );
};

export default YearPlannerAssetsActivityTable;
